import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	matchPath,
	Navigate,
	useLocation,
	useNavigate,
} from "react-router-dom";
import usePermission from "../hooks/usePermission";
import AccessDenied from "../components/AccessDenied";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useLazyCheckSessionQuery } from "../Redux/service/Users/AuthUsers";
import trans from "../utils";
import axios from "axios";
import Loader from "../components/Helpers/Loader/Loader";
import { useIdleTimer } from "react-idle-timer";
import { getData } from "../utils/IndexedDB";
import { setOfflineModules } from "../Redux/service/Users/UsersSlice";

export default function ProtectedRoutes({ children }) {
	const inf = useSelector((state) => state.users.user);
	const offlineMode = useSelector((state) => state.users.offlineMode);
	const authPermissions = useSelector(
		(state) => state.users.auth?.permissions ?? [],
	); // adjust if needed

	const { pathname } = useLocation();
	const navigate = useNavigate();

	// ✅ LAZY instead of always-on query (prevents stale 401 cache)
	const [checkSession, sessionRes] = useLazyCheckSessionQuery();

	const [sessionState, setSessionState] = useState({
		checked: false,
		valid: false,
		expired: false,
	});

	const didNotifyRef = useRef(false);
	const didLogoutRef = useRef(false);

	// --- Idle logout (unchanged) ---
	const IDLE_TIMEOUT = 30 * 60 * 1000;

	const onIdle = async () => {
		try {
			await axios.delete(
				`${process.env.REACT_APP_AUTH_APIS_URL}/users/logout`,
				{
					withCredentials: true,
				},
			);
		} catch (e) {
			// ignore
		} finally {
			localStorage.removeItem("persist:root");
			toast.dismiss("idle-logout-warning");
			toast.info(trans("You have been logged out due to inactivity."), {
				toastId: "idle-logout",
			});
			navigate("/signin", { replace: true });
		}
	};

	useIdleTimer({
		timeout: IDLE_TIMEOUT,
		promptTimeout: 60 * 1000,
		onPrompt: () => {
			toast.warning(
				trans("You will be logged out in 1 minute due to inactivity."),
				{
					toastId: "idle-logout-warning",
				},
			);
		},
		onIdle,
		debounce: 500,
	});

	// ✅ Run session check ONLY when user exists (or on first mount after login)
	useEffect(() => {
		let cancelled = false;

		const run = async () => {
			// if no user id => treat as logged out (don’t call session api)
			if (!inf?.id) {
				setSessionState({ checked: true, valid: false, expired: false });
				return;
			}

			setSessionState((s) => ({ ...s, checked: false }));

			try {
				const res = await checkSession().unwrap(); // expects {success:boolean}
				if (cancelled) return;

				const ok = res?.success === true;

				setSessionState({
					checked: true,
					valid: ok,
					expired: !ok,
				});
			} catch (err) {
				if (cancelled) return;

				// ✅ Only treat 401 as expired here (single source of truth)
				const status = err?.status;
				setSessionState({
					checked: true,
					valid: false,
					expired: status === 401,
				});
			}
		};

		run();

		return () => {
			cancelled = true;
		};
	}, [inf?.id, checkSession]);

	// ✅ logout when no inf.id (only once) – but don’t call session api
	const [logoutRedirect, setLogoutRedirect] = useState(false);
	useEffect(() => {
		if (!inf?.id && !didLogoutRef.current) {
			didLogoutRef.current = true;

			(async () => {
				try {
					await axios.delete(
						`${process.env.REACT_APP_AUTH_APIS_URL}/users/logout`,
						{
							withCredentials: true,
						},
					);
				} catch (e) {
					// ignore
				} finally {
					localStorage.removeItem("persist:root");
					setLogoutRedirect(true);
				}
			})();
		}
	}, [inf?.id]);

	//Check Cookie When PathName Change
	const checkingRef = useRef(false);
	const lastCheckAtRef = useRef(0);

	const runSessionCheck = async () => {
		if (!inf?.id) return;

		const now = Date.now();
		if (checkingRef.current) return;
		if (now - lastCheckAtRef.current < 5000) return;

		checkingRef.current = true;
		lastCheckAtRef.current = now;

		try {
			const res = await checkSession().unwrap();
			setSessionState({
				checked: true,
				valid: res?.success === true,
				expired: res?.success !== true,
			});
		} catch (err) {
			const status = err?.status;
			setSessionState({ checked: true, valid: false, expired: status === 401 });
		} finally {
			checkingRef.current = false;
		}
	};

	useEffect(() => {
		runSessionCheck();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inf?.id]);

	useEffect(() => {
		runSessionCheck();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	// --- Module mapping ---
	const PathToModuleKey = {
    "/": "SETUPACCOUNT",

    "/livetracking": "LIVETRACKING",
    "/new-livetracking": "LIVETRACKING",
    "/geofences": "GEOFENCES",
    "/historyplayback/:id": "LIVETRACKINGHISTORY",
    "/historical": "HISTORICAL",
    "/groups": "GROUPS",
    "/fleetmanagement/vehicles": "FMSVEHICLES",
    "/fleetmanagement/drivers": "FMSDRIVERS",
    "/notifications/all": "NOTIFICATIONS",
    "/notifications/configuration": "CONFIGURATION",
    "/reports": "ALLREPORTS",
    "/reports/": "ALLREPORTS",
    "/reports/Vehicles/:reportName": "REPORTSVEHICLES",
    "/reports/Vehicles/vehiclestrips": "REPORTSVEHICLESTRIPSREPORT",
    "/reports/Vehicles/speed": "REPORTSVEHICLESSPEEDREPORT",
    "/reports/Vehicles/stop": "REPORTSVEHICLESSTOPREPORT",
    "/reports/Vehicles/distance": "REPORTSVEHICLESDISTANCEREPORT",
    "/reports/Vehicles/devicebehavior": "REPORTSVEHICLESDEVICEBEHAVIORREPORT",
    "/reports/Vehicles/fuel": "REPORTSVEHICLESFUELREPORT",
    "/reports/Vehicles/odometer": "REPORTSVEHICLESODOMETERREPORT",
    "/reports/fieldservices/productivity": "REPORTSFSFSPRODUCTIVITY",
    "/reports/fieldservices/efficiency": "REPORTSFSTASKSPERFORMANCE",
    "/reports/fieldservices/requests": "REPORTSFSSERVICEREQUEST",
    "/reports/fieldservices/attendance": "REPORTSFSSHIFTCOMPLIANCE",
    "/reports/fieldservices/dailyAttendance": "REPORTSFSATTENDANCEREPORT",
    "/reports/fieldservices/deviceAssignment": "REPORTSFSDEVICEUSAGEREPORT",
    // "/reports/inventory/shelfAvailability": "REPORTSFSDEVICEUSAGEREPORT",
    "/reports/inventory/lostOpportunities": "DASHBOARDFS",
    "/streaming/live": "LIVESTREAM",
    "/streaming/playback": "PLAYBACK",
    "/mod-streaming": "MODLIVESTREAM",
    "/mod-playback": "MODPLAYBACK",
    "/forms/all": "FORMS",
    "/forms/formbuilder": "FORMS",
    "/forms/formbuilder/:id": "FORMS",
    "/forms/submission": "FORMSUBMISSIONS",
    "/facility-management/technicians": "FMS",
    "/tasks/tasks-board": "FIELD_TASKS",
    "/tasks/heatmap": "TASKS_HEATMAP",
    "/tasks/recurring-planners": "FIELD_TASKS",
    "/point-of-interest": "POIS",
    "/requests": "REQUESTS",
    "/IR": "IR",
    "/Submittals": "SUBMITTALS",
    "/snag-list": "SNAG",
    "/snag-report": "SNAGREPORT",
    "/setup/account": "SETUPACCOUNT",

    "/setup/roles&permissions": "SETUPUSERROLES",
    "/setup/roles&permissionsFS": "SETUPUSERROLESFS",
    "/setup/roles&permissionsQAQC": "SETUPUSERROLESQAQC",

    "/setup/project-setup": "QAQCPROJECTSETUP",
    "/setup/project-setup-control": "QAQCPROJECTSETUP",
    "/setup/companies": "SETUPCOMPANIES",
    "/setup/teams": "QAQCTEAMS",
    "/setup/offline-setup": "OFFLINECONFIGURATION",
    "/dashboard": "DASHBOARD",
    "/dashboard/qa": "DASHBOARDQAQC",
    "/dashboard/fsm": "DASHBOARDFS",
    "/dashboard/consultant": "CONSULTANTDASHBOARD",
    "/IR-offline-control": "IR",

    "/maintenance": "REGULARMAINTENANCE",
  };
	const getModuleKey = (path) => {
		if (PathToModuleKey[path]) return PathToModuleKey[path];
		for (const route in PathToModuleKey) {
			if (!route.includes("/:")) continue;
			const base = route.split("/:")[0];
			if (path.startsWith(base + "/")) return PathToModuleKey[route];
		}
		return undefined;
	};

	const moduleKey = getModuleKey(pathname);

	// ✅ must be called every render
	const { view, create, edit } = usePermission(moduleKey);

	const isDashboardPath = useMemo(() => {
		return (
			pathname === "/dashboard" ||
			pathname === "/dashboard/fsm" ||
			pathname === "/dashboard/consultant" ||
			pathname === "/dashboard/qa" ||
			pathname.startsWith("/dashboard/")
		);
	}, [pathname]);

	// ---------- RENDER GUARDS ----------
	const dispatch = useDispatch();
	const Offlinemodules = useSelector((state) => state.users.offlineModules);
	useEffect(() => {
		const initDB = async () => {
			try {
				const data = await getData(
					"OfflineModeData",
					"configurationStore",
					"configuration",
				);
				console.log("DbData", data);
				dispatch(setOfflineModules(data));
			} catch (error) {
				console.error("IndexedDB read error:", error);
			}
		};

		initDB();
	}, []);
	useEffect(() => {
		console.log("Offlinemodules in ProtectedRoutes", Offlinemodules);

		if (offlineMode && !Offlinemodules) return;

		const availableModules =
			Offlinemodules?.modules?.map((i) => i.module) || [];

		const hasSnag = availableModules.includes("SNAG");
		const hasIR = availableModules.includes("IR");

		const isIRRoute =
			matchPath("/IR", pathname) ||
			matchPath("/IR-offline-control/:id?", pathname);

		const isSnagRoute =
			matchPath("/snag-list", pathname) ||
			matchPath("/snag-offline-control/:id?", pathname);

		let isAllowed = true;

		if (offlineMode) {
			if (isIRRoute) isAllowed = hasIR;
			else if (isSnagRoute) isAllowed = hasSnag;
			else if (matchPath("/setup/offline-setup", pathname)) {
				isAllowed = true;
			} else {
				isAllowed = false;
			}

			if (!isAllowed) {
				navigate("/setup/offline-setup");
			}
		} else {
			if (
				matchPath("/snag-offline-control/:id?", pathname) ||
				matchPath("/IR-offline-control/:id?", pathname)
			) {
				navigate("/setup/account");
			}
		}

		console.log("Offline Mode:", offlineMode);
		console.log("Is Allowed:", isAllowed);
	}, [offlineMode, pathname, Offlinemodules]);
	console.log("OfflineMode", offlineMode);
	if (logoutRedirect) return <Navigate to="/signin" replace />;

	// ✅ while session is being checked (only when user exists)
	if (inf?.id && !sessionState.checked) {
		return (
			<div className="loader-container" style={{ height: "100vh" }}>
				<Loader />
			</div>
		);
	}

	// ✅ if session expired for real
	if (sessionState.expired) {
		if (!didNotifyRef.current) {
			toast.info(trans("Session Expired"), {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar",
			});
			didNotifyRef.current = true;
		}
		localStorage.removeItem("persist:root");
		return <Navigate to="/signin" replace />;
	}

	// if no moduleKey -> allow
	if (!moduleKey) return children;

	// dashboards always allow (you had that)
	if (isDashboardPath) return children;

	// permission checks
	if (!view && inf?.id) return <AccessDenied />;

	if (pathname === "/forms/formbuilder" && create === false)
		return <AccessDenied />;
	if (pathname.includes("formbuilder/") && edit === false)
		return <AccessDenied />;

	return children;
}
